"use client";

import { Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Auth_Form } from "@/components/auth/auth-form";
import {
  Auth_Panel_Class,
  Auth_Tab_Active,
  Auth_Tab_Idle,
} from "@/components/auth/auth-styles";

type Auth_Modal_Mode = "login" | "signup";

type Auth_Nav_Modal_Props = {
  Open: boolean;
  Mode: Auth_Modal_Mode;
  Next_Path: string;
  On_Close: () => void;
  On_Mode_Change: (Mode: Auth_Modal_Mode) => void;
  On_Success: () => void;
};

const Tab_Base =
  "rounded-md px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40";

export function Auth_Nav_Modal({
  Open,
  Mode,
  Next_Path,
  On_Close,
  On_Mode_Change,
  On_Success,
}: Auth_Nav_Modal_Props) {
  const [Mounted, Mounted_Set] = useState(false);

  useEffect(() => {
    Mounted_Set(true);
  }, []);

  useEffect(() => {
    if (!Open) {
      return;
    }

    function Escape_Handle(Event: KeyboardEvent) {
      if (Event.key === "Escape") {
        On_Close();
      }
    }

    const Overflow_Prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", Escape_Handle);

    return () => {
      document.body.style.overflow = Overflow_Prev;
      document.removeEventListener("keydown", Escape_Handle);
    };
  }, [Open, On_Close]);

  if (!Open || !Mounted) {
    return null;
  }

  const Subtitle =
    Mode === "login" ? "Sign in with your email and password." : "Verify email to create your account.";

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/40 backdrop-blur-sm"
        aria-label="Close account dialog"
        onClick={On_Close}
      />

      <div
        className="relative z-[101] flex max-h-[min(92vh,44rem)] w-full max-w-lg flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-nav-modal-title"
        onMouseDown={(Event) => Event.stopPropagation()}
      >
        <div className={`${Auth_Panel_Class} flex max-h-[min(92vh,44rem)] flex-col overflow-hidden`}>
          <div className="shrink-0 border-b border-pq-border px-6 pb-5 pt-6 sm:px-8 sm:pt-7">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2
                  id="auth-nav-modal-title"
                  className="font-display text-2xl font-semibold tracking-tight text-pq-fg sm:text-3xl"
                >
                  Account
                </h2>
                <div className="mt-2.5 h-0.5 w-12 rounded-full bg-gradient-to-r from-amber-500 to-amber-300" />
                <p className="mt-3 text-sm leading-relaxed text-pq-fg-muted">{Subtitle}</p>
              </div>
              <button
                type="button"
                onClick={On_Close}
                className="rounded-md border border-pq-border p-2 text-pq-fg-muted transition hover:border-pq-border-strong hover:bg-pq-bg-soft hover:text-pq-fg"
                aria-label="Close"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="mt-5 flex gap-1" role="tablist" aria-label="Account type">
              <button
                type="button"
                role="tab"
                aria-selected={Mode === "login"}
                onClick={() => On_Mode_Change("login")}
                className={`flex-1 ${Tab_Base} ${Mode === "login" ? Auth_Tab_Active : Auth_Tab_Idle}`}
              >
                Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={Mode === "signup"}
                onClick={() => On_Mode_Change("signup")}
                className={`flex-1 ${Tab_Base} ${Mode === "signup" ? Auth_Tab_Active : Auth_Tab_Idle}`}
              >
                Create account
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 sm:px-8 sm:py-6">
            <Suspense fallback={<p className="text-sm text-pq-fg-muted">Loading…</p>}>
              <Auth_Form
                key={Mode}
                Mode={Mode}
                Variant="modal"
                Next_Path={Next_Path}
                On_Success={On_Success}
                On_Mode_Change={On_Mode_Change}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
